import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

export default function Auth() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_token");
  const initialMode = resetToken ? "reset" : "login";
  const [mode, setMode] = useState(initialMode); // login | signup | forgot | reset
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resetToken) setMode("reset");
  }, [resetToken]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        if (!accepted) { toast.error("You must agree to the disclaimer."); return; }
        if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        await signup(name, email, password);
        toast.success("Welcome to the forge.");
        navigate("/dashboard");
      } else if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back.");
        navigate("/dashboard");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot", { email });
        toast.success("If that email is registered, a reset link is on its way.");
        setMode("login");
      } else if (mode === "reset") {
        if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (password !== confirm) { toast.error("Passwords don't match."); return; }
        await api.post("/auth/reset", { token: resetToken, password });
        toast.success("Password updated. Log in with your new password.");
        navigate("/auth", { replace: true });
        setMode("login");
        setPassword(""); setConfirm("");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Please wait a minute and try again.");
      } else {
        toast.error(err?.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const headlines = {
    login: { tag: "[ LOG IN ]", title: "Welcome back", sub: "Pick up where you left off." },
    signup: { tag: "[ NEW ATHLETE ]", title: "Join the forge", sub: "Create your account to save plans and orders." },
    forgot: { tag: "[ FORGOT PASSWORD ]", title: "Reset your password", sub: "We'll email you a one-time reset link." },
    reset: { tag: "[ SET NEW PASSWORD ]", title: "New password", sub: "Pick something strong. 8 characters minimum." },
  }[mode];

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2" data-testid="auth-page">
      <div className="relative hidden md:flex items-center justify-center bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3333]/10 via-transparent to-[#00E5FF]/10" />
        <div className="absolute top-10 left-10 font-jp kanji-vert text-9xl text-white/5 select-none">規律</div>
        <div className="absolute bottom-10 right-10 font-jp kanji-vert text-9xl text-[#FF3333]/10 select-none">浪人</div>
        <div className="relative z-10 px-12 max-w-md text-white">
          <Logo size={28} withText />
          <h2 className="font-heading uppercase text-5xl leading-[0.95] mt-10">Train in <span className="text-stroke-red">shadows</span></h2>
          <p className="font-sub text-white/50 mt-4 leading-relaxed">
            Members get saved AI plans, order history, and early access to every Waifu Collection drop.
          </p>
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/40 mt-10">規律 · 集中 · 力</div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 md:px-16 py-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF] mb-3">{headlines.tag}</div>
          <h1 className="font-heading uppercase text-5xl leading-none">{headlines.title}</h1>
          <p className="font-sub text-white/50 mt-3">{headlines.sub}</p>

          <div className="mt-10 space-y-5">
            {mode === "signup" && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/40">NAME</label>
                <input type="text" required data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00E5FF] outline-none py-2 font-sub text-lg transition-colors" />
              </div>
            )}
            {(mode === "login" || mode === "signup" || mode === "forgot") && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/40">EMAIL</label>
                <input type="email" required data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00E5FF] outline-none py-2 font-sub text-lg transition-colors" />
              </div>
            )}
            {(mode === "login" || mode === "signup" || mode === "reset") && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/40">{mode === "reset" ? "NEW PASSWORD" : "PASSWORD"}</label>
                <input type="password" required minLength={mode === "login" ? 6 : 8} data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00E5FF] outline-none py-2 font-sub text-lg transition-colors" />
              </div>
            )}
            {mode === "reset" && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/40">CONFIRM</label>
                <input type="password" required minLength={8} data-testid="auth-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00E5FF] outline-none py-2 font-sub text-lg transition-colors" />
              </div>
            )}
          </div>

          <button type="submit" disabled={loading || (mode === "signup" && !accepted)} data-testid="auth-submit" className="btn-forged-red mt-10 w-full disabled:opacity-50">
            {loading ? "Forging..." :
              mode === "login" ? "Log In" :
              mode === "signup" ? "Create Account" :
              mode === "forgot" ? "Send Reset Link" :
              "Update Password"}
          </button>

          {mode === "signup" && (
            <label className="flex items-start gap-3 mt-5 cursor-pointer group" data-testid="auth-disclaimer-ack">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="auth-disclaimer-checkbox" className="mt-1 w-4 h-4 accent-[#FF3333] flex-shrink-0" />
              <span className="font-sub text-xs text-white/60 leading-relaxed group-hover:text-white/80">
                I confirm I am 18+ and have read and agree to the{" "}
                <Link to="/legal" target="_blank" className="text-[#FF3333] underline">Disclaimer &amp; Liability Release</Link>.
                All content is for educational purposes only and I use the site at my own risk.
              </span>
            </label>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-center">
            {mode !== "reset" && mode !== "forgot" && (
              <button type="button" data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "signup" : "login")} className="font-mono text-xs text-white/40 hover:text-[#00E5FF] tracking-widest">
                {mode === "login" ? "→ NO ACCOUNT? CREATE ONE" : "← ALREADY A MEMBER? LOG IN"}
              </button>
            )}
            {mode === "login" && (
              <button type="button" data-testid="auth-forgot" onClick={() => setMode("forgot")} className="font-mono text-xs text-white/40 hover:text-[#FF3333] tracking-widest">
                FORGOT PASSWORD?
              </button>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <button type="button" data-testid="auth-back-login" onClick={() => setMode("login")} className="font-mono text-xs text-white/40 hover:text-[#00E5FF] tracking-widest">
                ← BACK TO LOG IN
              </button>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="font-mono text-[10px] text-white/30 tracking-widest hover:text-white/60">← BACK HOME</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
